'use client';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface OnboardingContextValue {
  showOnboarding: boolean;
  showTour: boolean;
  startTour: () => void;
  completeOnboarding: () => void;
  skipOnboarding: () => void;
  dontShowOnboardingAgain: () => void;
  completeTour: () => void;
  closeTour: () => void;
  resetOnboarding: () => Promise<void>;
  resetTour: () => void;
}

const OnboardingContext = createContext<OnboardingContextValue>({} as OnboardingContextValue);

export const useOnboarding = () => useContext(OnboardingContext);

export function OnboardingProvider({ children }: { children: React.ReactNode }) {
  const { profile, user } = useAuth();
  const supabase = createClient();

  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [settingsId, setSettingsId] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  const upsertSettings = useCallback(async (patch: Record<string, unknown>) => {
    if (!profile?.id) return;
    if (settingsId) {
      await supabase.from('user_settings').update(patch).eq('id', settingsId);
    } else {
      const { data } = await supabase
        .from('user_settings')
        .upsert({ user_id: profile.id, ...patch }, { onConflict: 'user_id' })
        .select('id')
        .single();
      if (data?.id) setSettingsId(data.id);
    }
  }, [profile?.id, settingsId, supabase]);

  // Load onboarding state when profile is available
  useEffect(() => {
    if (!profile?.id || initialized) return;

    const load = async () => {
      try {
        const { data } = await supabase
          .from('user_settings')
          .select('id, onboarding_completed')
          .eq('user_id', profile.id)
          .maybeSingle();

        if (data) {
          setSettingsId(data.id);
          // Show onboarding only if not completed
          if (!data.onboarding_completed) {
            setShowOnboarding(true);
          }
        } else {
          // No settings row yet — new user, show onboarding
          setShowOnboarding(true);
        }
      } catch {
        // If column doesn't exist yet (migration pending), show onboarding
        setShowOnboarding(true);
      }
      setInitialized(true);
    };

    load();
  }, [profile?.id, initialized, supabase]);

  const completeOnboarding = useCallback(async () => {
    setShowOnboarding(false);
    await upsertSettings({ onboarding_completed: true });
  }, [upsertSettings]);

  const skipOnboarding = useCallback(() => {
    setShowOnboarding(false);
  }, []);

  const dontShowOnboardingAgain = useCallback(async () => {
    setShowOnboarding(false);
    await upsertSettings({ onboarding_completed: true });
  }, [upsertSettings]);

  const startTour = useCallback(() => {
    setShowTour(true);
  }, []);

  const completeTour = useCallback(async () => {
    setShowTour(false);
    await upsertSettings({ tour_completed: true });
  }, [upsertSettings]);

  const closeTour = useCallback(() => {
    setShowTour(false);
  }, []);

  const resetOnboarding = useCallback(async () => {
    await upsertSettings({ onboarding_completed: false });
    setShowOnboarding(true);
    setInitialized(false);
  }, [upsertSettings]);

  const resetTour = useCallback(() => {
    setShowTour(true);
  }, []);

  return (
    <OnboardingContext.Provider
      value={{
        showOnboarding,
        showTour,
        startTour,
        completeOnboarding,
        skipOnboarding,
        dontShowOnboardingAgain,
        completeTour,
        closeTour,
        resetOnboarding,
        resetTour,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}
